import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { fmtTL, todayIso } from "@/lib/format";
import { Plus, Trash2 } from "lucide-react";

const PAYMENT_METHODS = ["Nakit", "Havale/EFT", "Çek", "Kredi Kartı", "Vadeli"];
const SHIPPING_METHODS = ["Kargo", "Nakliye", "Elden Teslim", "Kendi Aracımız", "Tedarikçi Teslim"];
const STATUS_OPTIONS = [
  { value: "draft", label: "Taslak" },
  { value: "sent", label: "Gönderildi" },
  { value: "received", label: "Teslim Alındı" },
  { value: "cancelled", label: "İptal" },
];

const emptyItem = () => ({
  product_code: "", product_name: "", quantity: 1,
  unit_price: 0, discount_percent: 0, vat_percent: 20, unit: "Adet",
});

const empty = () => ({
  tracking_no: "",
  order_date: todayIso(),
  supplier_name: "",
  supplier_contact: "",
  supplier_phone: "",
  delivery_address: "",
  delivery_date: "",
  payment_method: "",
  payment_term_days: 0,
  shipping_method: "",
  notes: "",
  items: [emptyItem()],
  status: "draft",
});

const Section = ({ title, children }) => (
  <div className="azd-card p-6">
    <div className="text-sm font-bold text-[#08285A] mb-4 uppercase tracking-wider">{title}</div>
    {children}
  </div>
);

const Field = ({ label, required, children, className = "" }) => (
  <div className={className}>
    <label className="block text-xs font-semibold text-[#111827] mb-1.5">
      {label} {required && <span className="text-[#DC2626]">*</span>}
    </label>
    {children}
  </div>
);

function lineTotal(it) {
  const gross = Number(it.quantity || 0) * Number(it.unit_price || 0);
  const disc = gross * Number(it.discount_percent || 0) / 100;
  const afterDisc = gross - disc;
  const vat = afterDisc * Number(it.vat_percent || 0) / 100;
  return { gross, disc, afterDisc, vat, total: afterDisc + vat };
}

export default function PurchaseOrderForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      api.get(`/purchase-orders/${id}`).then((r) => setForm({ ...empty(), ...r.data }))
        .catch(() => { toast.error("Sipariş bulunamadı"); navigate("/purchase-orders"); });
    }
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setForm((f) => {
    const items = [...f.items];
    items[i] = { ...items[i], [k]: v };
    return { ...f, items };
  });
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (i) => setForm((f) => ({
    ...f,
    items: f.items.length > 1 ? f.items.filter((_, x) => x !== i) : f.items,
  }));

  // Totals
  let subtotal = 0, discountTotal = 0, vatTotal = 0;
  form.items.forEach((it) => {
    const t = lineTotal(it);
    subtotal += t.gross; discountTotal += t.disc; vatTotal += t.vat;
  });
  const grand = subtotal - discountTotal + vatTotal;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.supplier_name.trim()) return toast.error("Tedarikçi adı gerekli");
    for (const it of form.items) {
      if (!it.product_name.trim()) return toast.error("Ürün adı boş bırakılamaz");
      if (Number(it.quantity) <= 0) return toast.error("Miktar 0'dan büyük olmalı");
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tracking_no: form.tracking_no.trim() || null,
        delivery_date: form.delivery_date || null,
        payment_term_days: Number(form.payment_term_days) || 0,
        items: form.items.map((it) => ({
          product_code: it.product_code || "",
          product_name: it.product_name.trim(),
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price),
          discount_percent: Number(it.discount_percent) || 0,
          vat_percent: Number(it.vat_percent) || 0,
          unit: it.unit || "Adet",
        })),
      };
      if (isEdit) {
        await api.put(`/purchase-orders/${id}`, payload);
        toast.success("Sipariş güncellendi");
      } else {
        await api.post("/purchase-orders", payload);
        toast.success("Satın alma siparişi kaydedildi");
      }
      navigate("/purchase-orders");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5 max-w-6xl">
      <Section title="Sipariş Bilgileri">
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Sipariş Takip No (boş bırakılırsa otomatik)">
            <Input value={form.tracking_no} onChange={(e) => set("tracking_no", e.target.value)}
                   placeholder="SAT-2026-00001" data-testid="po-tracking-no" />
          </Field>
          <Field label="Sipariş Tarihi" required>
            <Input type="date" value={form.order_date}
                   onChange={(e) => set("order_date", e.target.value)} required
                   data-testid="po-order-date" />
          </Field>
          <Field label="Durum">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="po-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </Section>

      <Section title="Tedarikçi Bilgileri">
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Tedarikçi Adı / Firma" required>
            <Input value={form.supplier_name} onChange={(e) => set("supplier_name", e.target.value)}
                   required data-testid="po-supplier" />
          </Field>
          <Field label="İlgili Kişi">
            <Input value={form.supplier_contact}
                   onChange={(e) => set("supplier_contact", e.target.value)}
                   data-testid="po-supplier-contact" />
          </Field>
          <Field label="Telefon">
            <Input value={form.supplier_phone}
                   onChange={(e) => set("supplier_phone", e.target.value)}
                   data-testid="po-supplier-phone" />
          </Field>
        </div>
      </Section>

      <Section title="Ürünler">
        <div className="overflow-x-auto azd-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FB] text-[10px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-2 py-2 font-semibold">Ürün Kodu</th>
                <th className="text-left px-2 py-2 font-semibold">Ürün Adı *</th>
                <th className="text-right px-2 py-2 font-semibold">Miktar *</th>
                <th className="text-left px-2 py-2 font-semibold">Birim</th>
                <th className="text-right px-2 py-2 font-semibold">Fiyat *</th>
                <th className="text-right px-2 py-2 font-semibold">İskonto %</th>
                <th className="text-right px-2 py-2 font-semibold">KDV %</th>
                <th className="text-right px-2 py-2 font-semibold">Satır Toplamı</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => {
                const t = lineTotal(it);
                return (
                  <tr key={i} data-testid={`po-item-${i}`} className="border-t border-[#E5E7EB]">
                    <td className="px-2 py-2">
                      <Input value={it.product_code} onChange={(e) => setItem(i, "product_code", e.target.value)}
                             placeholder="Kod" className="h-9" />
                    </td>
                    <td className="px-2 py-2">
                      <Input value={it.product_name} onChange={(e) => setItem(i, "product_name", e.target.value)}
                             placeholder="Ürün adı" className="h-9"
                             data-testid={`po-item-name-${i}`} required />
                    </td>
                    <td className="px-2 py-2 w-24">
                      <Input type="number" min="0" step="0.01" value={it.quantity}
                             onChange={(e) => setItem(i, "quantity", e.target.value)}
                             className="h-9 text-right"
                             data-testid={`po-item-qty-${i}`} required />
                    </td>
                    <td className="px-2 py-2 w-24">
                      <Input value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)}
                             placeholder="Adet" className="h-9" />
                    </td>
                    <td className="px-2 py-2 w-28">
                      <Input type="number" min="0" step="0.01" value={it.unit_price}
                             onChange={(e) => setItem(i, "unit_price", e.target.value)}
                             className="h-9 text-right"
                             data-testid={`po-item-price-${i}`} required />
                    </td>
                    <td className="px-2 py-2 w-20">
                      <Input type="number" min="0" max="100" step="0.01" value={it.discount_percent}
                             onChange={(e) => setItem(i, "discount_percent", e.target.value)}
                             className="h-9 text-right" />
                    </td>
                    <td className="px-2 py-2 w-20">
                      <Input type="number" min="0" max="100" step="0.01" value={it.vat_percent}
                             onChange={(e) => setItem(i, "vat_percent", e.target.value)}
                             className="h-9 text-right" />
                    </td>
                    <td className="px-2 py-2 text-right font-bold text-[#0B3676]"
                        data-testid={`po-item-total-${i}`}>
                      {fmtTL(t.total)}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button type="button" onClick={() => removeItem(i)}
                              disabled={form.items.length === 1}
                              className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded disabled:opacity-40"
                              title="Sil">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Button type="button" variant="outline" onClick={addItem}
                data-testid="po-add-item" className="mt-3">
          <Plus size={14} className="mr-1" /> Ürün Ekle
        </Button>

        {/* Totals */}
        <div className="mt-5 grid md:grid-cols-4 gap-3 bg-[#F6F8FB] border border-[#E5E7EB] rounded-lg p-4">
          <Kpi label="Ara Toplam" value={fmtTL(subtotal)} color="text-[#08285A]" />
          <Kpi label="İskonto" value={fmtTL(discountTotal)} color="text-[#DC2626]" />
          <Kpi label="KDV" value={fmtTL(vatTotal)} color="text-[#F59E0B]" />
          <Kpi label="Genel Toplam" value={fmtTL(grand)} color="text-[#0B3676]" bold testid="po-grand" />
        </div>
      </Section>

      <Section title="Teslim & Ödeme Bilgileri">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Field label="Teslim Adresi">
              <Textarea value={form.delivery_address}
                        onChange={(e) => set("delivery_address", e.target.value)}
                        rows={2} data-testid="po-delivery-address" />
            </Field>
          </div>
          <Field label="Teslim Tarihi">
            <Input type="date" value={form.delivery_date}
                   onChange={(e) => set("delivery_date", e.target.value)}
                   data-testid="po-delivery-date" />
          </Field>
          <Field label="Nakliye Şekli">
            <Select value={form.shipping_method || "__none__"}
                    onValueChange={(v) => set("shipping_method", v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="po-shipping"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Belirtilmedi</SelectItem>
                {SHIPPING_METHODS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Ödeme Şekli">
            <Select value={form.payment_method || "__none__"}
                    onValueChange={(v) => set("payment_method", v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="po-payment"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Belirtilmedi</SelectItem>
                {PAYMENT_METHODS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Vade Günü">
            <Input type="number" min="0" value={form.payment_term_days}
                   onChange={(e) => set("payment_term_days", e.target.value)}
                   data-testid="po-term-days" />
          </Field>
        </div>
      </Section>

      <Section title="Not (Opsiyonel)">
        <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                  rows={3} placeholder="Sipariş ile ilgili notlar..." data-testid="po-notes" />
      </Section>

      <div className="flex gap-3 justify-end">
        <Button type="button" variant="outline" onClick={() => navigate("/purchase-orders")}>
          Vazgeç
        </Button>
        <Button type="submit" disabled={saving} data-testid="po-save"
                className="bg-[#0B3676] hover:bg-[#08285A] text-white">
          {saving ? "Kaydediliyor..." : isEdit ? "Güncelle" : "Siparişi Kaydet"}
        </Button>
      </div>
    </form>
  );
}

function Kpi({ label, value, color, bold, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-bold">{label}</div>
      <div className={`${bold ? "text-2xl" : "text-lg"} font-extrabold ${color}`} data-testid={testid}>
        {value}
      </div>
    </div>
  );
}
